import React, {useEffect, useState} from 'react';
import {ActivityIndicator, FlatList, Platform, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react';

import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import apiService from '../transport/apiService';
import {OrderLine} from '../transport/apiServiceResponse';
import {visibleScrollbarProps} from '../utils/webScrollbar';

import Button from './themed/Button';
import Text from './ui-controls/Text';
import {ProductTileModal} from './ProductTile';

type CatalogueAddToCartModalProps = {
  productIds: string[];
  onClose: () => void;
};

const CatalogueAddToCartModal = observer(({productIds, onClose}: CatalogueAddToCartModalProps) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const [products, setProducts] = useState<OrderLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const fetchedProducts = await Promise.all(
          productIds.map(id => apiService.getProductById(id)),
        );
        const foundProducts = fetchedProducts.filter(Boolean);

        if (foundProducts.length) {
          setProducts(foundProducts);
          setError(null);
        } else {
          setError('These products are not in stock in this store right now.');
          setProducts([]);
        }
      } catch (_err) {
        setError('Something went wrong finding your products.');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [productIds]);

  const containerStyle = styles.container;

  if (loading) {
    return (
      <View style={containerStyle}>
        <ActivityIndicator style={styles.loader} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={containerStyle}>
        <View style={styles.errorContainer}>
          <Text>{error}</Text>
          <Text>Please try again later.</Text>
        </View>
        <View style={styles.button}>
          <Button title="Close" onPress={onClose} />
        </View>
      </View>
    );
  }

  return (
    <View style={containerStyle}>
      <FlatList
        data={products}
        renderItem={({item}) => (
          <ProductTileModal
            item={item}
            disabled
            testContext="catalogue-modal"
            containerStyle={styles.tile}
          />
        )}
        keyExtractor={item => `${item.id}-0`}
        horizontal
        showsHorizontalScrollIndicator={Platform.OS === 'web'}
        // Only desktop web forces the always-visible bar — mobile web keeps the
        // auto-hiding indicator it has today.
        {...visibleScrollbarProps(isWebDesktopScreen)}
        style={styles.flatList}
        contentContainerStyle={styles.flatListContent}
      />
      <View style={styles.button}>
        <Button title="Close" onPress={onClose} />
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  loader: {
    paddingVertical: 40,
  },
  errorContainer: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  flatList: {
    flexGrow: 0,
  },
  flatListContent: {
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  tile: {
    marginHorizontal: 8,
    marginBottom: 16,
  },
  button: {
    paddingVertical: 16,
  },
});

export default CatalogueAddToCartModal;
